/**
 * Даты без времени — строки `YYYY-MM-DD`. Арифметика в UTC, поэтому переходы на летнее/зимнее
 * время на неё не влияют. «Сегодня» всегда считается в часовом поясе пользователя.
 */

export type DateStr = string;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isDate(v: unknown): v is DateStr {
  if (typeof v !== 'string' || !DATE_RE.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

export function isTime(v: unknown): v is string {
  return typeof v === 'string' && TIME_RE.test(v);
}

const pad = (n: number) => String(n).padStart(2, '0');

function toUtc(s: DateStr): number {
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function fromUtc(ms: number): DateStr {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function addDays(s: DateStr, n: number): DateStr {
  return fromUtc(toUtc(s) + n * 864e5);
}

/** Сколько дней от a до b (b − a). */
export function diffDays(a: DateStr, b: DateStr): number {
  return Math.round((toUtc(b) - toUtc(a)) / 864e5);
}

/** День недели 0 = воскресенье … 6 = суббота. */
export function weekday(s: DateStr): number {
  return new Date(toUtc(s)).getUTCDay();
}

function parts(tz: string, now: Date) {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const p = Object.fromEntries(f.formatToParts(now).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** Сегодняшняя дата в часовом поясе пользователя. */
export function todayIn(tz: string, now: Date = new Date()): DateStr {
  return parts(tz, now).date;
}

/** Текущее время `HH:MM` в часовом поясе пользователя. */
export function timeIn(tz: string, now: Date = new Date()): string {
  return parts(tz, now).time;
}

/**
 * Момент (UTC) для локальных даты и времени в часовом поясе tz.
 * Для несуществующего времени (перевод часов вперёд) берётся ближайшее следующее.
 */
export function zonedToUtc(date: DateStr, time: string, tz: string): Date {
  const [h, mi] = time.split(':').map(Number) as [number, number];
  const guess = toUtc(date) + (h * 60 + mi) * 60_000;
  // Две итерации поправки на смещение пояса достаточно, в том числе в дни перевода часов.
  let t = guess;
  for (let i = 0; i < 2; i++) {
    const p = parts(tz, new Date(t));
    const [ph, pm] = p.time.split(':').map(Number) as [number, number];
    const local = toUtc(p.date) + (ph * 60 + pm) * 60_000;
    t += guess - local;
  }
  return new Date(t);
}

export const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

/** «2 окт». */
export function fmtDate(s: DateStr | null | undefined): string {
  if (!isDate(s)) return s ?? '';
  const [, m, d] = s.split('-').map(Number) as [number, number, number];
  return `${d} ${MONTHS_SHORT[m - 1]}`;
}

/** «Сегодня, 14:00», «Завтра», «Вчера» или «2 окт». */
export function whenText(
  d: DateStr | null | undefined,
  t: string | null | undefined,
  today: DateStr,
): string {
  if (!isDate(d)) return '';
  const n = diffDays(today, d);
  const day = n === 0 ? 'Сегодня' : n === 1 ? 'Завтра' : n === -1 ? 'Вчера' : fmtDate(d);
  return day + (t ? `, ${t}` : '');
}

/** «1 день», «3 дня», «5 дней». */
export function plural(n: number, one: string, few: string, many: string): string {
  const m = n % 10;
  const h = n % 100;
  const w = m === 1 && h !== 11 ? one : m >= 2 && m <= 4 && (h < 10 || h >= 20) ? few : many;
  return `${n} ${w}`;
}

export const days = (n: number) => plural(n, 'день', 'дня', 'дней');
